import React, { useEffect, useState } from "react";
import { MonitorSmartphone, ShieldCheck, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { getMyDevices, forgetDevice, formatApiError } from "@/lib/apiClient";

const when = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "");

export const KnownDevices = () => {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = () => getMyDevices().then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const forget = async (d) => {
    setBusy(d.id);
    try {
      await forgetDevice(d.id);
      toast.success("Device forgotten — the next sign-in from it will trigger a security email");
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
    setBusy(null);
  };

  return (
    <div className="rounded-lg border border-border p-3 space-y-2" data-testid="known-devices">
      <div className="flex items-center gap-2 text-xs font-medium"><ShieldCheck className="h-3.5 w-3.5 text-emerald-500" /> Devices that have signed in</div>
      <p className="text-[11px] text-muted-foreground">You get an email whenever your account signs in from a device or country not on this list.</p>
      {rows === null && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
      {rows?.length === 0 && <div className="text-xs text-muted-foreground" data-testid="known-devices-empty">No devices recorded yet — your next sign-in will register this one.</div>}
      {rows?.length > 0 && (
        <ul className="divide-y divide-border max-h-44 overflow-y-auto d20-scroll">
          {rows.map((d) => (
            <li key={d.id} className="flex items-center gap-2 py-1.5 text-xs" data-testid={`device-${d.id}`} data-current={d.current ? "true" : "false"}>
              <MonitorSmartphone className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium">{d.agent} {d.current && <span className="ml-1 rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] text-emerald-600 dark:text-emerald-400" data-testid="device-current-tag">This device</span>}</div>
                <div className="truncate text-muted-foreground">{d.location} · {d.last_ip || "ip unknown"} · last {when(d.last_seen_at)}{d.logins > 1 ? ` · ${d.logins} sign-ins` : ""}</div>
              </div>
              {!d.current && (
                <button type="button" onClick={() => forget(d)} disabled={busy === d.id} className="rounded p-1 text-muted-foreground hover:text-red-500" title="Forget this device" data-testid={`device-forget-${d.id}`}>
                  {busy === d.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
